import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useT } from "@/i18n";
import { LangToggle } from "@/components/LangToggle";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { apiErr } from "@/lib/api";
import { RO_COUNTIES, PHONE_PREFIXES } from "@/lib/counties";
import { toast } from "sonner";
import { Loader2, ArrowRight, User, Building2 } from "lucide-react";

export default function Register() {
  const { register } = useAuth();
  const { t } = useT();
  const navigate = useNavigate();
  const [f, setF] = useState({
    client_type: "fizica", full_name: "", email: "", password: "",
    phone: "", phone_prefix: "+40", county: "", company_name: "", cui: "", reg_com: "",
    gdpr_consent: false, marketing_consent: false,
  });
  const [loading, setLoading] = useState(false);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!f.gdpr_consent) return toast.error(t("Consimțământul GDPR este obligatoriu."));
    if (!f.county) return toast.error(t("Selectează județul."));
    setLoading(true);
    try {
      await register(f);
      toast.success(t("Cont creat! Poți genera prima randare."));
      navigate("/studio");
    } catch (err) {
      toast.error(apiErr(err));
    } finally {
      setLoading(false);
    }
  };

  const TypeBtn = ({ value, icon: Icon, label }) => (
    <button type="button" onClick={() => set("client_type", value)}
      data-testid={`client-type-${value}`}
      className={`flex items-center gap-3 rounded-2xl border p-4 transition-all ${
        f.client_type === value ? "border-primary bg-primary/10 glow-red" : "border-slate-700 bg-slate-900 hover:border-slate-600"
      }`}>
      <span className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${f.client_type === value ? "border-primary" : "border-slate-600"}`}>
        {f.client_type === value && <span className="w-2.5 h-2.5 rounded-full bg-primary" />}
      </span>
      <Icon className="w-5 h-5 text-slate-300" />
      <span className="text-white font-medium">{label}</span>
    </button>
  );

  return (
    <div className="min-h-screen klus-grid-bg flex flex-col items-center justify-center px-4 py-10 relative">
      <div className="absolute top-4 right-4"><LangToggle /></div>
      <Link to="/" className="mb-6"><Logo className="h-9" /></Link>
      <div className="w-full max-w-2xl animate-fade-up">
        <div className="bg-card border border-slate-800 rounded-3xl p-6 sm:p-8">
          <h1 className="text-3xl sm:text-4xl font-extrabold text-white mb-1">{t("Creează cont")}</h1>
          <p className="text-slate-400 text-sm mb-6">{t("Câteva date și ești gata să generezi prima randare.")}</p>

          <form onSubmit={submit} className="space-y-5">
            <div>
              <Label className="text-xs uppercase tracking-widest text-slate-400 mb-2 block">{t("Tip client")}</Label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <TypeBtn value="fizica" icon={User} label={t("Persoană fizică")} />
                <TypeBtn value="juridica" icon={Building2} label={t("Persoană juridică")} />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-xs uppercase tracking-widest text-slate-400">{t("Nume complet")}</Label>
                <Input required value={f.full_name} onChange={(e) => set("full_name", e.target.value)}
                  data-testid="register-name" className="mt-1.5 bg-slate-900 border-slate-700 h-12" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-slate-400">{t("Email")}</Label>
                <Input type="email" required value={f.email} onChange={(e) => set("email", e.target.value)}
                  data-testid="register-email" className="mt-1.5 bg-slate-900 border-slate-700 h-12" />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-xs uppercase tracking-widest text-slate-400">{t("Parolă (min. 6)")}</Label>
                <Input type="password" required minLength={6} value={f.password} onChange={(e) => set("password", e.target.value)}
                  data-testid="register-password" className="mt-1.5 bg-slate-900 border-slate-700 h-12" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-slate-400">{t("Telefon")}</Label>
                <div className="flex gap-2 mt-1.5">
                  <Select value={f.phone_prefix} onValueChange={(v) => set("phone_prefix", v)}>
                    <SelectTrigger data-testid="register-prefix" className="w-28 bg-slate-900 border-slate-700 h-12">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {PHONE_PREFIXES.map((p) => <SelectItem key={p.code} value={p.code}>{p.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Input value={f.phone} onChange={(e) => set("phone", e.target.value)}
                    data-testid="register-phone" placeholder="7xx xxx xxx" className="flex-1 bg-slate-900 border-slate-700 h-12" />
                </div>
              </div>
            </div>

            {f.client_type === "juridica" && (
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 animate-fade-up">
                <div>
                  <Label className="text-xs uppercase tracking-widest text-slate-400">{t("Firmă")}</Label>
                  <Input value={f.company_name} onChange={(e) => set("company_name", e.target.value)}
                    data-testid="register-company" className="mt-1.5 bg-slate-900 border-slate-700 h-12" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest text-slate-400">CUI</Label>
                  <Input value={f.cui} onChange={(e) => set("cui", e.target.value)}
                    data-testid="register-cui" className="mt-1.5 bg-slate-900 border-slate-700 h-12" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest text-slate-400">{t("Reg. Com.")}</Label>
                  <Input value={f.reg_com} onChange={(e) => set("reg_com", e.target.value)}
                    data-testid="register-regcom" className="mt-1.5 bg-slate-900 border-slate-700 h-12" />
                </div>
              </div>
            )}

            <div>
              <Label className="text-xs uppercase tracking-widest text-slate-400">{t("Județ *")}</Label>
              <Select value={f.county} onValueChange={(v) => set("county", v)}>
                <SelectTrigger data-testid="register-county" className="mt-1.5 bg-slate-900 border-slate-700 h-12">
                  <SelectValue placeholder={t("— alege județul —")} />
                </SelectTrigger>
                <SelectContent className="max-h-72">
                  {RO_COUNTIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
              <p className="text-[11px] text-slate-500 mt-1">{t("Util pentru a-ți repartiza un partener KLUS din zona ta.")}</p>
            </div>

            <div className="space-y-3 pt-1">
              <label className="flex items-start gap-3 cursor-pointer">
                <Checkbox checked={f.gdpr_consent} onCheckedChange={(v) => set("gdpr_consent", !!v)}
                  data-testid="register-gdpr" className="mt-0.5 border-slate-600 data-[state=checked]:bg-primary data-[state=checked]:border-primary" />
                <span className="text-sm text-slate-300 leading-relaxed">
                  {t("Sunt de acord cu prelucrarea datelor mele cu caracter personal de către")} <b className="text-white">GLOW SENSE S.R.L.</b> {t("conform GDPR pentru contactare comercială și statistici interne. Vezi")}{" "}
                  <Link to="/confidentialitate" className="text-primary underline">{t("politica de confidențialitate")}</Link>. <span className="text-primary">*</span>
                </span>
              </label>
              <label className="flex items-start gap-3 cursor-pointer">
                <Checkbox checked={f.marketing_consent} onCheckedChange={(v) => set("marketing_consent", !!v)}
                  data-testid="register-marketing" className="mt-0.5 border-slate-600 data-[state=checked]:bg-primary data-[state=checked]:border-primary" />
                <span className="text-sm text-slate-300 leading-relaxed">
                  {t("Doresc să primesc oferte și recomandări de design de la KLUS (opțional, poate fi retras oricând).")}
                </span>
              </label>
            </div>

            <Button type="submit" disabled={loading} data-testid="register-submit"
              className="w-full h-13 py-3.5 bg-primary hover:bg-primary/90 text-white text-base">
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <>{t("Creează cont")} <ArrowRight className="w-4 h-4 ml-1.5" /></>}
            </Button>
          </form>

          <p className="text-sm text-slate-400 mt-6 text-center">
            {t("Ai deja cont?")}{" "}
            <Link to="/login" className="text-primary hover:underline" data-testid="goto-login">{t("Autentifică-te")}</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
